import { css } from '@emotion/core';
import { Button, DialogActions, DialogContent, Paper, Typography } from '@material-ui/core';
import React, { useEffect } from 'react';

import { ConflictProfileWithDetails } from 'shared/types/conflict-profiles';

import { getProfileConflictDetails, mergeProfiles, resolveProfileConflict } from '../api';
import { logger } from '../common/logger';
import { ProfileDataTransfer } from '../components/profile-data-transfer';
import { LoadingModal } from '../core-ui/loading-modal';
import { Spinner } from '../core-ui/spinner';
import { useModal } from '../hooks/use-modal';
import { PersonProfile } from '../sfc/person-profile';

const messageStyles = css`
    flex: 1 1 auto;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const styles = css`
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    .content {
        flex: 1 1 auto;
        display: flex;
        margin-top: 20px;
        overflow: hidden;

        .merge-profile-content {
            padding: 0 2px;
            overflow: auto;
            margin-right: 10px;
            flex: 1 1;

            &::-webkit-scrollbar-thumb {
                border-radius: 2px;
            }

            .detail-title {
                font-weight: 500;
            }
        }
    }
`;

interface ProfileConflictResolverProps {
    personId1: string;
    personId2: string;
    readOnly?: boolean;
    onClose: () => void;
    onResolved: (mergedPersonId?: string) => void;
    onConflictClosed?: (mergedPersonId?: string) => void;
    children?: React.ReactNode;
}

const pollIntervalMs = 10000;

/** Manual resolution uses pair-specific API access and waits for an active investigation to finish. */
export const ProfileConflictResolver: React.FC<ProfileConflictResolverProps> = (props) => {
    const [currentConflict, setCurrentConflict] = React.useState<ConflictProfileWithDetails>(null);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [error, setError] = React.useState<Error>();
    const [pollError, setPollError] = React.useState<Error>();
    const [loadingModal, setLoadingModal] = React.useState<boolean>(false);
    const [transferDialogOpen, setTransferDialogOpen] = React.useState(false);
    const { getConfirmation } = useModal();
    const displayedError = error || pollError;
    const readOnly =
        props.readOnly || currentConflict?.resolution.processing || !currentConflict?.resolution.conflictOpen;

    useEffect(() => {
        let cancelled = false;
        let timer: ReturnType<typeof setTimeout>;
        setLoading(true);
        setError(undefined);
        setPollError(undefined);
        const loadDetails = async () => {
            try {
                const result = await getProfileConflictDetails(props.personId1, props.personId2);
                if (cancelled) return;
                if (!result.success) throw new Error(result.data?.message || 'Error loading conflicting profiles');
                setPollError(undefined);
                setCurrentConflict(result.data);
                if (!result.data.resolution.conflictOpen) {
                    props.onConflictClosed?.(result.data.resolution.mergedPersonId);
                }
                if (result.data.resolution.conflictOpen) timer = setTimeout(loadDetails, pollIntervalMs);
            } catch (err) {
                if (cancelled) return;
                setPollError(err);
                logger.error(err);
                timer = setTimeout(loadDetails, pollIntervalMs);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        loadDetails();
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [props.personId1, props.personId2]);

    const handleOpenTransferDataDialog = () => setTransferDialogOpen(true);
    // Transfers persist immediately; returning to the comparison does not undo them.
    const handleCloseTransferDialog = () => setTransferDialogOpen(false);

    const handleResolveConflict = async () => {
        setLoadingModal(true);
        const { personIds } = currentConflict;
        try {
            handleCloseTransferDialog();
            const result = await resolveProfileConflict(personIds[0], personIds[1]);
            if (!result.success) throw new Error(result.data?.message || 'Unable to resolve conflict');
            props.onResolved();
        } catch (err) {
            setError(err);
            logger.error('Error reprocessing profiles', err);
        } finally {
            setLoadingModal(false);
        }
    };

    const mergeProfileData = () => {
        let [from, to] = currentConflict.personIds;
        if (
            currentConflict.details[to].candidates.length === 0 &&
            currentConflict.details[from].candidates.length > 0
        ) {
            [from, to] = [to, from];
        }
        getConfirmation(
            async () => {
                setLoadingModal(true);
                try {
                    const result = await mergeProfiles(from, to);
                    if (!result.success) throw new Error(result.data?.message || 'Unable to merge profiles');
                    props.onResolved(result.data.mergedId);
                } catch (err) {
                    setError(err);
                    logger.error(err, { message: 'Error merging profiles' });
                } finally {
                    setLoadingModal(false);
                }
            },
            'These profiles will be merged in to a single profile. This CANNOT be reversed',
            'Are you sure to merge these profiles?'
        );
    };

    const handleResolve = () =>
        getConfirmation(
            handleResolveConflict,
            'Close this conflict without merging profiles or moving more data?',
            'Resolve conflict'
        );

    let content;
    const actions =
        !readOnly && currentConflict && !loading && !displayedError ? (
            <>
                <Button disabled={loadingModal} onClick={handleOpenTransferDataDialog}>
                    Transfer data
                </Button>
                <Button disabled={loadingModal} onClick={mergeProfileData}>
                    Merge profiles
                </Button>
                <Button disabled={loadingModal} onClick={handleResolve}>
                    Resolve without changes
                </Button>
            </>
        ) : null;
    const processingMessage = currentConflict?.resolution.processing ? (
        <Typography>Agent is processing this conflict. Human actions are unavailable until it finishes.</Typography>
    ) : null;

    if (loading) {
        content = <Spinner />;
    } else if (displayedError) {
        content = <div css={messageStyles}>{displayedError.message}</div>;
    } else if (currentConflict && !currentConflict.resolution.conflictOpen) {
        content = <Typography>This conflict is no longer open.</Typography>;
    } else if (currentConflict) {
        const { personIds, details } = currentConflict;
        const left = personIds[0];
        const right = personIds[1];
        const leftProfile = details[left].profile;
        const rightProfile = details[right].profile;
        const profileNotFound = <div>Profile content not found</div>;

        const leftProfileComponent = leftProfile ? (
            <PersonProfile personId={left} profile={leftProfile?.content} viewedAt={leftProfile?.viewedAt} />
        ) : (
            profileNotFound
        );
        const rightProfileComponent = rightProfile ? (
            <PersonProfile personId={right} profile={rightProfile?.content} viewedAt={rightProfile?.viewedAt} />
        ) : (
            profileNotFound
        );

        content = (
            <div id="content" className="flex-fill">
                <div css={styles}>
                    <div className="content">
                        {loadingModal ? <LoadingModal /> : null}
                        <div className="merge-profile-content">
                            <Paper className="detailed-profile-card">
                                <div className="card-content">
                                    <div className="detail-title">
                                        <a href={`/person/${details[left].person?.id}`} target="_blank">
                                            {details[left].person.name.full}
                                        </a>
                                    </div>
                                    <div>{details[left].candidates.length} Jobs</div>
                                </div>
                            </Paper>
                            {leftProfileComponent}
                        </div>
                        <div className="merge-profile-content">
                            <Paper className="detailed-profile-card">
                                <div className="card-content">
                                    <div className="detail-title">
                                        <a target="_blank" href={`/person/${details[right].person.id}`}>
                                            {details[right].person.name.full}
                                        </a>
                                    </div>
                                    <div>{details[right].candidates.length} Jobs</div>
                                </div>
                            </Paper>
                            {rightProfileComponent}
                        </div>
                    </div>
                </div>
            </div>
        );
    } else {
        content = <div css={messageStyles}>No profiles found</div>;
    }
    const transferDialog =
        transferDialogOpen && currentConflict?.resolution.conflictOpen ? (
            <ProfileDataTransfer
                personId1={props.personId1}
                personId2={props.personId2}
                profiles={currentConflict}
                readOnly={readOnly}
                onClose={handleCloseTransferDialog}
                onResolved={props.onResolved}
            />
        ) : null;
    return (
        <>
            <DialogContent dividers={true}>
                {props.children}
                {processingMessage}
                <div className="profile-comparison">{content}</div>
            </DialogContent>
            <DialogActions>
                {actions}
                <Button disabled={loadingModal} onClick={props.onClose}>
                    Close
                </Button>
            </DialogActions>
            {transferDialog}
        </>
    );
};
